import { useEffect, useState } from "react";
import { Toaster } from "react-hot-toast";

import CallManager from "./components/CallManager";
import WorkingStatusModal from "./components/WorkingStatusModal";
import AppRoutes from "./routes/AppRoutes";
import { useAuth } from "./app/store";
import { connectSocket } from "./socket/socket";
import { isAuthSessionCurrent } from "./utils/authSession";
import { usePunchReminder } from "./utils/usePunchReminder";

const SOCKET_BOOT_DELAY_MS = 500;
const SESSION_CHECK_INTERVAL_MS = 60 * 1000;

export default function App() {
  const [theme] = useState(
    () => localStorage.getItem("theme") || "dark"
  );
  const [socketReady, setSocketReady] = useState(false);
  const { token, logout } = useAuth();

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("theme", theme);
  }, [theme]);

  useEffect(() => {
    connectSocket();

    const socketBootTimer = window.setTimeout(() => {
      setSocketReady(true);
    }, SOCKET_BOOT_DELAY_MS);

    return () => window.clearTimeout(socketBootTimer);
  }, []);

  useEffect(() => {
    if ("Notification" in window && Notification.permission !== "granted") {
      Notification.requestPermission().then((permission) => {
        console.info("[notifications] Permission:", permission);
      });
    }
  }, []);

  useEffect(() => {
    if (!token) return undefined;

    const redirectToLoginIfExpired = () => {
      if (isAuthSessionCurrent()) return;

      logout();
      if (window.location.pathname !== "/login") {
        window.location.replace("/login");
      }
    };

    redirectToLoginIfExpired();

    const checkWhenVisible = () => {
      if (document.visibilityState === "visible") redirectToLoginIfExpired();
    };

    const intervalId = window.setInterval(
      redirectToLoginIfExpired,
      SESSION_CHECK_INTERVAL_MS
    );

    window.addEventListener("focus", redirectToLoginIfExpired);
    window.addEventListener("pageshow", redirectToLoginIfExpired);
    document.addEventListener("visibilitychange", checkWhenVisible);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", redirectToLoginIfExpired);
      window.removeEventListener("pageshow", redirectToLoginIfExpired);
      document.removeEventListener("visibilitychange", checkWhenVisible);
    };
  }, [logout, token]);

  usePunchReminder();

  return (
    <>
      <Toaster position="top-right" containerStyle={{ zIndex: 999999 }} />
      {socketReady && <CallManager />}
      <WorkingStatusModal />
      <AppRoutes />
    </>
  );
}
